import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { MAX_FORMS_PER_TASK_TYPE } from '@skewb-works/contracts';
import { FileText, Plus, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useFormList } from '../hooks/useFormBuilder';
import {
  useCreateTaskType,
  useSetTaskForms,
  useTaskForms,
  useUpdateTaskType,
} from '../hooks/useJobManagement';

interface SelectedForm {
  formId: number;
  formName: string;
}

export interface EditTaskTypeDialogProps {
  mode: 'closed' | 'create' | 'edit';
  editingId: number | null;
  initialName: string;
  onClose: () => void;
}

export function EditTaskTypeDialog({
  mode,
  editingId,
  initialName,
  onClose,
}: EditTaskTypeDialogProps) {
  const createMutation = useCreateTaskType();
  const updateMutation = useUpdateTaskType();
  const setTaskFormsMutation = useSetTaskForms();

  const [name, setName] = useState('');
  const [selectedForms, setSelectedForms] = useState<SelectedForm[]>([]);
  const [formSearch, setFormSearch] = useState('');
  // Legacy task types may have more than one form attached from before the
  // one-form limit; surface that instead of dropping them silently on save.
  const [extraFormCount, setExtraFormCount] = useState(0);

  const { data: formListData } = useFormList();
  const { data: taskFormsData, isLoading: taskFormsLoading } = useTaskForms(editingId);

  const allForms = formListData?.values ?? [];

  // Reset form state whenever the dialog opens or switches between modes
  useEffect(() => {
    if (mode === 'closed') return;
    setName(mode === 'edit' ? initialName : '');
    setSelectedForms([]);
    setFormSearch('');
    setExtraFormCount(0);
  }, [mode, editingId]);

  // Populate forms from the server once the query resolves. Task types are
  // limited to one form, but historic rows may still have several attached — keep
  // only the first by sequence so the dialog shows what will actually be saved,
  // rather than silently dropping extras on save with no visible warning.
  useEffect(() => {
    if (editingId && taskFormsData?.data) {
      setSelectedForms(
        taskFormsData.data.slice(0, MAX_FORMS_PER_TASK_TYPE).map((tf) => ({
          formId: tf.formId,
          formName: tf.formName ?? `Form #${tf.formId}`,
        })),
      );
      setExtraFormCount(Math.max(0, taskFormsData.data.length - MAX_FORMS_PER_TASK_TYPE));
    }
  }, [editingId, taskFormsData]);

  const selectedForm = selectedForms[0] ?? null;

  const availableForms = useMemo(() => {
    const selectedIds = new Set(selectedForms.map((f) => f.formId));
    let pool = allForms.filter((f: any) => !selectedIds.has(f.id));
    if (formSearch) {
      const q = formSearch.toLowerCase();
      pool = pool.filter((f: any) => f.name?.toLowerCase().includes(q));
    }
    return pool;
  }, [allForms, selectedForms, formSearch]);

  // A task type is limited to one form for now (MAX_FORMS_PER_TASK_TYPE), so
  // picking a form replaces the current selection rather than appending. The
  // underlying task_form table still stores a list, so lifting the limit later
  // only means restoring the multi-select UI.
  function selectForm(formId: number, formName: string) {
    setSelectedForms([{ formId, formName }]);
    setFormSearch('');
  }

  function removeForm(formId: number) {
    setSelectedForms((prev) => prev.filter((f) => f.formId !== formId));
  }

  async function handleSave() {
    let taskTypeId = editingId;

    if (editingId) {
      await updateMutation.mutateAsync({ id: editingId, body: { name } });
    } else {
      const result = await createMutation.mutateAsync({ name });
      taskTypeId = result.id;
    }

    if (taskTypeId) {
      await setTaskFormsMutation.mutateAsync({
        taskTypeId,
        forms: selectedForms.map((f, idx) => ({
          formId: f.formId,
          sequence: idx + 1,
        })),
      });
    }

    onClose();
  }

  const isSaving =
    createMutation.isPending || updateMutation.isPending || setTaskFormsMutation.isPending;

  return (
    <Dialog open={mode !== 'closed'} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{mode === 'edit' ? 'Edit Task Type' : 'Add Task Type'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="tt-name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="tt-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter task type name"
            />
          </div>

          {/* Attached Form — exactly one per task type for now. */}
          <div className="space-y-2">
            <Label>
              Attached Form <span className="text-red-500">*</span>
            </Label>
            <p className="text-xs text-muted-foreground">
              A task type has one form. Selecting a form replaces the current one.
            </p>

            {selectedForm && (
              <div className="border rounded-lg">
                <div className="flex items-center gap-2 px-3 py-2">
                  <FileText className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                  <span className="flex-1 text-sm text-foreground">{selectedForm.formName}</span>
                  <button
                    type="button"
                    onClick={() => removeForm(selectedForm.formId)}
                    disabled={taskFormsLoading}
                    className="p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-destructive"
                    title="Remove"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}

            {/* Pick a form */}
            <div className="border rounded-lg overflow-hidden">
              <div className="p-2 border-b bg-muted/30">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input
                    placeholder={selectedForm ? 'Search to replace form...' : 'Search forms...'}
                    value={formSearch}
                    onChange={(e) => setFormSearch(e.target.value)}
                    className="pl-8 h-8 text-sm"
                  />
                </div>
              </div>
              <div className="max-h-[150px] overflow-y-auto">
                {availableForms.length === 0 ? (
                  <div className="p-3 text-sm text-muted-foreground text-center">
                    {formSearch ? 'No forms match your search' : 'No forms available'}
                  </div>
                ) : (
                  availableForms.map((f: any) => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => selectForm(f.id, f.name)}
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/50 text-left text-sm"
                      disabled={taskFormsLoading}
                    >
                      <Plus className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-foreground">{f.name}</span>
                    </button>
                  ))
                )}
              </div>
            </div>

            {!taskFormsLoading && !selectedForm && (
              <p className="text-xs text-red-500">A form must be attached</p>
            )}

            {extraFormCount > 0 && (
              <p className="text-xs text-amber-600">
                This task type previously had {extraFormCount + MAX_FORMS_PER_TASK_TYPE} forms
                attached. Saving keeps only the form shown above and detaches the other{' '}
                {extraFormCount === 1 ? 'one' : extraFormCount}.
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!name.trim() || !selectedForm || isSaving || taskFormsLoading}
          >
            {isSaving ? 'Saving...' : mode === 'edit' ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
